import React, { useState, useEffect } from "react";
import { Card, Text } from "@tremor/react";
import { CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getTeamTasks, getProjectTasks } from "../../Requests/TaskRequests";
import { getTeamUuid } from "../../utils/cookies";
import TaskKanbanBoard from "./TaskKanbanBoard";

export default function TasksWidget({ project = null, preloadedData = null, showProjectBadge = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBoard, setShowBoard] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    
    // Use preloaded data if available (performance optimization)
    if (preloadedData) {
      try {
        setLoading(true);
        setTasks(Array.isArray(preloadedData) ? preloadedData : []);
      } catch (err) {
        console.error("Error processing preloaded tasks:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      loadTasks();
    }
  }, [project?.uuid, preloadedData?.length]); // Use stable references to prevent infinite loops

  const loadTasks = async () => {
    try {
      setLoading(true);
      let response;
      
      if (project) {
        // Load project-specific tasks
        response = await getProjectTasks(project.uuid);
      } else {
        // Load team tasks (for Dashboard usage)
        const teamUuid = getTeamUuid();
        response = await getTeamTasks(teamUuid);
      }
      
      // Handle different response structures
      const tasksData = response.data?.tasks || response.data?.data?.tasks || response.data || [];
      
      setTasks(Array.isArray(tasksData) ? tasksData : []);
    } catch (err) {
      console.error("Error loading tasks:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupTasksByUser = (tasks) => {
    const grouped = {};
    
    // Ensure tasks is an array
    if (!Array.isArray(tasks)) {
      console.warn("groupTasksByUser received non-array:", tasks);
      return [];
    }
    
    tasks.forEach(task => {
      const userId = task.assignee?.id || 'unassigned';
      const userName = task.assignee?.name || t('Tasks.Unassigned');
      
      if (!grouped[userId]) {
        grouped[userId] = {
          user: { id: userId, name: userName },
          tasks: []
        };
      }
      
      grouped[userId].tasks.push(task);
    });
    
    return Object.values(grouped);
  };

  const getStatusIcon = (status) => {
    switch (status?.name?.toLowerCase()) {
      case 'done':
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case 'in progress':
        return <ClockIcon className="w-4 h-4 text-yellow-600" />;
      default:
        return <XCircleIcon className="w-4 h-4 text-gray-400" />;
    }
  };

  if (showBoard) {
    return (
      <TaskKanbanBoard 
        onClose={() => setShowBoard(false)}
        tasks={tasks}
        onTasksUpdate={setTasks}
      />
    );
  }

  const groupedTasks = groupTasksByUser(tasks);

  return (
    <div className="h-full flex flex-col">
      {/* Main content area - matches other bento grid sections */}
      <div className="h-80 bg-white p-6">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <Text className="text-red-600">{t('Tasks.ErrorLoading')}</Text>
          </div>
        ) : groupedTasks.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <ClockIcon className="w-8 h-8 text-gray-400" />
            </div>
            <Text className="text-gray-500">{t('Tasks.NoTasks')}</Text>
          </div>
        ) : (
          <div className="space-y-4 overflow-y-auto h-full">
            {groupedTasks.map(({ user, tasks: userTasks }) => (
              <div key={user.id} className="border-l-4 border-iavisus-100 pl-4">
                <div className="flex items-center justify-between mb-2">
                  <Text className="font-medium text-gray-700">
                    {user.name}
                  </Text>
                  <span className="bg-iavisus-50 text-iavisus-600 text-xs px-2 py-1 rounded-full">
                    {userTasks.length}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {userTasks.slice(0, 3).map(task => (
                    <div key={task.id} data-testid={`task-item-${task.uuid || task.id}`} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <Text className="text-sm font-medium text-gray-900 truncate">
                            {task.title}
                          </Text>
                          {showProjectBadge && task.project?.name && (
                            <span data-testid="task-project-badge" className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full truncate inline-block mt-1">
                              {task.project.name}
                            </span>
                          )}
                          {task.due_date && (
                            <Text className="text-xs text-gray-500 mt-1">
                              {t('Tasks.DueDate')}: {new Date(task.due_date).toLocaleDateString()}
                            </Text>
                          )}
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          {getStatusIcon(task.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {userTasks.length > 3 && (
                    <Text className="text-xs text-gray-500 text-center">
                      {t('Tasks.AndMore', { count: userTasks.length - 3 })}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Bottom text area - matches other bento grid sections */}
      <div className="p-10 pt-4 flex flex-col flex-1">
        <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('Tasks.Title')}</p>
        <p className="mt-2 max-w-lg text-sm/6 text-gray-600">
          {project ? t('Tasks.ProjectSubtitle') : t('Tasks.Subtitle')}
        </p>
        <div className="mt-auto flex justify-end">
          <button
            onClick={() => project ? navigate(`/project/${project.uuid}/kanban`) : setShowBoard(true)}
            className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100"
          >
            {t('Tasks.ViewBoard')}
          </button>
        </div>
      </div>
    </div>
  );
}